import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { apiHelpers } from '../../services/api';
import ChartWidget from '../../components/charts/ChartWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const STORAGE_KEY_PREFIX = 'neoshift-public-token-';

function formatTimeAgo(seconds) {
  if (seconds < 60) return 'just now';
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} min${m !== 1 ? 's' : ''} ago`;
  }
  const h = Math.floor(seconds / 3600);
  return `${h} hr${h !== 1 ? 's' : ''} ago`;
}

export default function PublicDashboard() {
  const { slug } = useParams();
  const [token, setToken] = useState(() => sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${slug}`));
  const [dashboard, setDashboard] = useState(null);
  const [charts, setCharts] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState(null);
  const cacheTimestampsRef = useRef({});

  const loadDashboard = useCallback(async () => {
    if (!slug || !token) return;
    try {
      setIsLoading(true);
      const response = await apiHelpers.getPublicDashboard(slug, token);
      const { dashboard: d, charts: c } = response.data;
      setDashboard(d);
      setCharts(c || []);
      setLayout(d?.layout || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${slug}`);
        setToken(null);
      }
      console.error('Failed to load public dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  }, [slug, token]);

  useEffect(() => {
    if (token) {
      loadDashboard();
    } else {
      setIsLoading(false);
    }
  }, [token, loadDashboard]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const form = e.target;
    const username = form.username?.value?.trim();
    const password = form.password?.value;
    if (!username || !password) {
      setLoginError('Please enter username and password');
      return;
    }
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await apiHelpers.publicDashboardLogin(slug, username, password);
      const { token: newToken } = response.data;
      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${slug}`, newToken);
      setToken(newToken);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Invalid username or password');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${slug}`);
    setToken(null);
    setDashboard(null);
    setCharts([]);
    setLayout([]);
  }, [slug]);

  const chartDataFetcher = useCallback(
    (chartId) => apiHelpers.getPublicChartData(slug, chartId, token).then((res) => {
      const cacheAge = res.data?.cacheAge ?? 0;
      cacheTimestampsRef.current[chartId] = {
        fetchedAt: Date.now(),
        cacheAge,
      };
      return res;
    }).catch((err) => {
      if (err.response?.status === 401) {
        handleLogout();
      }
      throw err;
    }),
    [slug, token, handleLogout]
  );

  useEffect(() => {
    if (!token || !dashboard) return;

    const computeLabel = () => {
      const entries = Object.values(cacheTimestampsRef.current);
      if (entries.length === 0) return;
      let oldestAge = 0;
      for (const { fetchedAt, cacheAge } of entries) {
        const ageNow = cacheAge + (Date.now() - fetchedAt) / 1000;
        if (ageNow > oldestAge) oldestAge = ageNow;
      }
      setLastUpdatedLabel(formatTimeAgo(Math.round(oldestAge)));
    };

    computeLabel();
    const interval = setInterval(computeLabel, 30000);
    return () => clearInterval(interval);
  }, [token, dashboard, charts]);

  // No token: show login form
  if (!token) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm card p-8">
          <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            Sign in to view dashboard
          </h1>
          <p className="text-surface-500 dark:text-surface-400 text-sm mb-6">
            Enter your credentials to access this shared dashboard.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="input w-full"
                placeholder="Enter username"
                disabled={isLoggingIn}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="input w-full"
                placeholder="Enter password"
                disabled={isLoggingIn}
              />
            </div>
            {loginError && (
              <p className="text-red-500 text-sm">{loginError}</p>
            )}
            <button type="submit" className="btn-primary w-full" disabled={isLoggingIn}>
              {isLoggingIn ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
        <p className="mt-8 text-surface-500 dark:text-surface-400 text-sm">
          Powered by Neoshift
        </p>
      </div>
    );
  }

  // Loading dashboard
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <footer className="py-4 text-center text-surface-500 dark:text-surface-400 text-sm">
          Powered by Neoshift
        </footer>
      </div>
    );
  }

  const gridLayout = layout.map(item => ({
    i: item.chartId?.toString() || item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: 2,
    minH: 2
  }));

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col">
      <header className="border-b border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 px-6 py-4">
        <div className="flex items-center justify-between max-w-[1600px] mx-auto">
          <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white">
            {dashboard?.name || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4">
            {lastUpdatedLabel && (
              <span className="text-xs text-surface-400 dark:text-surface-500">
                Data updated: {lastUpdatedLabel}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="text-sm text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full">
        {charts.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="text-surface-500 dark:text-surface-400">This dashboard has no charts.</p>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 768, md: 480, sm: 0 }}
            cols={{ lg: 12, md: 6, sm: 1 }}
            rowHeight={50}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={false}
            isResizable={false}
          >
            {charts.map(chart => (
              <div key={chart._id} className="card overflow-hidden h-full">
                <ChartWidget
                  chart={chart}
                  readOnly={true}
                  chartDataFetcher={chartDataFetcher}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </main>

      <footer className="py-4 text-center text-surface-500 dark:text-surface-400 text-sm border-t border-surface-200 dark:border-surface-700">
        Powered by Neoshift
      </footer>
    </div>
  );
}
